import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeartHandshake, Plus, X, RefreshCw, MapPin, Clock, User, Route, Play,
  CheckCircle2, Ban, StickyNote,
} from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import usePermissions from '../../hooks/usePermissions';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import TimeSelect from '../../components/common/TimeSelect';

// HOME VISITS (Phase 5, #6) — the admin-dispatched Care Taker board. Pick a
// day, see every visit grouped by care taker, dispatch new ones, and move a
// visit through its states. Patient self-booking with pricing/billing is the
// module's own phase 2 — nothing here bills.

const today = () => new Date().toISOString().slice(0, 10);

// No system id on screen (owner, 29 Sep 2026). The server falls back to the
// id when it has no name for a person; treat that as "no name" and let the
// caller draw a neutral word instead.
const realName = (name, id) => (name && String(name) !== String(id || '') ? name : '');

const STATUS_META = {
  scheduled: { tint: 'bg-slate-100 text-slate-600', labelKey: 'care.scheduled', fallback: 'Scheduled' },
  en_route: { tint: 'bg-sky-50 text-sky-700', labelKey: 'care.en_route', fallback: 'En route' },
  started: { tint: 'bg-amber-50 text-amber-700', labelKey: 'care.started', fallback: 'Started' },
  completed: { tint: 'bg-emerald-50 text-emerald-700', labelKey: 'care.completed', fallback: 'Completed' },
  cancelled: { tint: 'bg-gray-100 text-gray-400', labelKey: 'care.cancelled', fallback: 'Cancelled' },
};

// What each state may become NEXT, mirrored from the server's machine; the
// server remains the referee.
const NEXT = {
  scheduled: ['en_route', 'started', 'cancelled'],
  en_route: ['started', 'cancelled'],
  started: ['completed'],
};
const NEXT_META = {
  en_route: { Icon: Route, labelKey: 'care.mark_en_route', fallback: 'En route' },
  started: { Icon: Play, labelKey: 'care.mark_started', fallback: 'Start' },
  completed: { Icon: CheckCircle2, labelKey: 'care.mark_completed', fallback: 'Complete' },
  cancelled: { Icon: Ban, labelKey: 'care.mark_cancelled', fallback: 'Cancel' },
};

const CareVisits = () => {
  const { t } = useTranslation();
  const { can, allowAll } = usePermissions();
  const canDispatch = allowAll || can('care.visit.dispatch');

  const [date, setDate] = useState(today());
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const axiosInstance = createAuthedAxios();
      const { data } = await axiosInstance.get(`${baseURL}/care-visits?date=${date}`);
      setVisits(data.visits || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('care.load_failed', 'Could not load the visits'));
    } finally {
      setLoading(false);
    }
  }, [date, t]);

  useEffect(() => { load(); }, [load]);

  const move = async (visit, status) => {
    let cancelReason;
    if (status === 'cancelled') {
      cancelReason = window.prompt(t('care.cancel_why', 'Why is this visit cancelled?')) || '';
      if (!cancelReason.trim()) return;
    }
    setBusyId(visit.visitId);
    try {
      const axiosInstance = createAuthedAxios();
      await axiosInstance.post(`${baseURL}/care-visits/${visit.visitId}/status`, { status, cancelReason });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('care.move_failed', 'Could not update the visit'));
    } finally {
      setBusyId(null);
    }
  };

  const byCaretaker = useMemo(() => {
    const groups = new Map();
    for (const v of visits) {
      if (!groups.has(v.caretakerId)) groups.set(v.caretakerId, { key: v.caretakerId, name: realName(v.caretakerName, v.caretakerId) || t('tenantpage.staff', 'Staff'), visits: [] });
      groups.get(v.caretakerId).visits.push(v);
    }
    return [...groups.values()];
  }, [visits, t]);

  return (
    <div data-testid="care-visits-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
            <HeartHandshake size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">{t('care.title', 'Home visits')}</h1>
            <p className="text-xs text-gray-400">{t('care.sub', 'Dispatch care takers, follow every visit through the day')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all" data-testid="care-date" />
          <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer" title={t('common.refresh', 'Refresh')}>
            <RefreshCw size={17} />
          </button>
          {canDispatch && (
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl text-sm font-semibold shadow-md hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all cursor-pointer"
              data-testid="care-dispatch-open"
            >
              <Plus size={16} /> {t('care.dispatch', 'Dispatch visit')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse h-28 bg-white rounded-2xl shadow-sm" />)}
        </div>
      ) : visits.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <HeartHandshake size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-500">{t('care.none', 'No visits on this day.')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {byCaretaker.map((group) => (
            <div key={group.key || group.name} className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#9DB3B1]/20 text-[#2D8A8A] flex items-center justify-center font-bold text-xs">
                  {(group.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <p className="text-sm font-bold text-gray-800">{group.name}</p>
                <span className="text-[11px] text-gray-400">{group.visits.length} {t('care.visits', 'visits')}</span>
              </div>
              <div className="divide-y divide-gray-50">
                {group.visits.map((v) => {
                  const meta = STATUS_META[v.status] || STATUS_META.scheduled;
                  const nexts = (NEXT[v.status] || []).filter((n) => n !== 'cancelled' || canDispatch);
                  return (
                    <div key={v.visitId} className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                            <User size={13} className="text-gray-400" /> {realName(v.patientName, v.patientId) || t('common.patient_label', 'Patient')}
                          </p>
                          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${meta.tint}`}>
                            {t(meta.labelKey, meta.fallback)}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Clock size={11} />
                            <span data-testid={`care-visit-time-${v.visitId}`}>{formatTime(v.scheduledAt)}</span></span>
                          {v.address && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {v.address}</span>}
                          {v.notes && <span className="inline-flex items-center gap-1"><StickyNote size={11} /> {v.notes}</span>}
                        </p>
                        {v.visitNotes && (
                          <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5">{v.visitNotes}</p>
                        )}
                      </div>
                      {nexts.length > 0 && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          {nexts.map((n) => {
                            const nm = NEXT_META[n];
                            const danger = n === 'cancelled';
                            return (
                              <button
                                key={n}
                                onClick={() => move(v, n)}
                                disabled={busyId === v.visitId}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50 ${danger ? 'bg-rose-50 text-rose-600 hover:bg-rose-100' : 'bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20'}`}
                                data-testid={`care-move-${v.visitId}-${n}`}
                              >
                                <nm.Icon size={13} /> {t(nm.labelKey, nm.fallback)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <DispatchModal
          onClose={() => setShowNew(false)}
          onDone={() => { setShowNew(false); load(); }}
          defaultDate={date}
        />
      )}
    </div>
  );
};

const DispatchModal = ({ onClose, onDone, defaultDate }) => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ patientId: '', caretakerId: '', time: '09:00', notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const axiosInstance = createAuthedAxios();
    (async () => {
      try {
        const { data } = await axiosInstance.get(`${baseURL}/patients?page=1&limit=100`);
        setPatients(data.patients || data.rows || []);
      } catch { /* the picker degrades to manual ids */ }
      try {
        const { data } = await axiosInstance.get(`${baseURL}/staff?limit=100`);
        setStaff(data.staff || []);
      } catch { /* same */ }
    })();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const axiosInstance = createAuthedAxios();
      await axiosInstance.post(`${baseURL}/care-visits`, {
        patientId: form.patientId,
        caretakerId: form.caretakerId,
        scheduledAt: new Date(`${defaultDate}T${form.time}:00`).toISOString(),
        notes: form.notes.trim() || undefined,
      });
      toast.success(t('care.dispatched', 'Visit dispatched'));
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('care.dispatch_failed', 'Could not dispatch the visit'));
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="care-dispatch-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="border-b border-gray-100 p-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-800">{t('care.dispatch', 'Dispatch visit')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3">
          <select value={form.patientId} onChange={(e) => setForm((f) => ({ ...f, patientId: e.target.value }))}
            className={`${input} cursor-pointer`} data-testid="care-pick-patient">
            <option value="">{t('care.pick_patient', 'Patient…')}</option>
            {patients.map((p) => (
              <option key={p.patientId} value={p.patientId}>
                {[[p.name || p.firstName, p.surname].filter(Boolean).join(' ') || t('common.patient_label', 'Patient'), hospitalPatientCode(p)].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
          <select value={form.caretakerId} onChange={(e) => setForm((f) => ({ ...f, caretakerId: e.target.value }))}
            className={`${input} cursor-pointer`} data-testid="care-pick-caretaker">
            <option value="">{t('care.pick_caretaker', 'Care taker…')}</option>
            {staff.map((s) => (
              <option key={s.staffId} value={s.staffId}>
                {[s.name, s.surname].filter(Boolean).join(' ') || t('tenantpage.staff', 'Staff')}{s.roleName ? ` — ${s.roleName}` : ''}
              </option>
            ))}
          </select>
          <TimeSelect
            value={form.time}
            onChange={(time) => setForm((f) => ({ ...f, time }))}
            testId="care-pick-time"
            ariaLabel={t('care.visit_time', 'Visit time')}
          />
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder={t('care.notes_placeholder', 'Instruction for the care taker (optional)')} rows={2} className={input} />
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2.5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.patientId || !form.caretakerId}
            className="px-5 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl text-sm font-semibold shadow-md hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer"
            data-testid="care-dispatch-confirm"
          >
            {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" />}
            {t('care.dispatch', 'Dispatch visit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareVisits;
